import React from 'react';
import { withPropsOnChange, compose } from 'recompose';
import { Route, Redirect, Switch } from 'react-router-dom';
import { Auth, ApiClient } from 'services';
import { Container, Row, Col } from 'reactstrap';
import { getPermission, NO_ACCESS, READ_ONLY } from 'permissions';
import { Installer } from 'config/roles';
import { Page } from 'components/common';

import {
  Admin,
  Login,
  Users,
  Installers,
  WhiteLabelPartners,
  Orders,
  Jobs,
  Profile,
  InstallerOrders,
  InstallerRules,
  Pricing,
} from './pages';

const enhanceComponent = compose(
  // pass a new API client into each route
  withPropsOnChange([''], ({ client }) => ({ client: client || ApiClient() }))
);

const PublicRoute = ({ component, ...props }) => {
  const EnhancedComponent = enhanceComponent(component);

  return (
    <Route {...props} render={props => <EnhancedComponent {...props} />} />
  );
};

const PrivateRoute = ({ component, fluid, ...props }) => {
  const EnhancedComponent = enhanceComponent(component);
  const role = props.role ? props.role : Auth.getRole();
  const resetPassword = props.resetPassword ? props.resetPassword : false;
  const permission = getPermission(props.path.split('/')[1], role);

  if (permission === NO_ACCESS) {
    return <Redirect to={'/'} />;
  }
  const readOnly = permission === READ_ONLY;

  return (
    <Route
      {...props}
      render={props =>
        Auth.loggedIn() ? (
          resetPassword ? (
            <Redirect
              to={{
                pathname: '/profile',
                state: { from: props.location },
              }}
            />
          ) : (
            <Container fluid={fluid}>
              <Row>
                <Col>
                  <EnhancedComponent readOnly={readOnly} role={role} {...props} />
                </Col>
              </Row>
            </Container>
          )
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

const Routes = ({ role, resetPassword }) => {
  const isInstaller = role === Installer;

  return (
    <Switch>
      <PublicRoute exact path="/login" component={Login} role={role} />

      <PrivateRoute exact path="/profile" component={Profile} role={role} />
      <PrivateRoute
        exact
        path="/manage-job/:id"
        component={Jobs.Manage}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/admin"
        component={Admin}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/white-label-partners"
        component={WhiteLabelPartners.List}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/white-label-partners/:id"
        component={WhiteLabelPartners.Edit}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/users"
        component={Users.List}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/users/:id"
        component={Users.Edit}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/installers"
        component={Installers.List}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/installers/:id"
        component={Installers.Edit}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/installers/:id/users/:id"
        component={Users.NewInstallerUser}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/installer-users"
        component={Users.InstallerList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/installer-user/:id"
        component={Users.NewInstallerUser}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/new-orders"
        component={InstallerOrders.NewList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/all-orders"
        component={(isInstaller ? InstallerOrders : Orders).AllList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/unassigned-orders"
        component={(isInstaller ? InstallerOrders : Orders).UnassignedList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/assigned-orders"
        component={(isInstaller ? InstallerOrders : Orders).AssignedList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/rejected-orders"
        component={Orders.RejectedList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/completed-jobs"
        component={(isInstaller ? InstallerOrders : Orders).CompletedList}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        fluid
        path="/installer-rules"
        component={InstallerRules.List}
        role={role}
        resetPassword={resetPassword}
      />
      <PrivateRoute
        exact
        path="/installer-rules/:id"
        component={InstallerRules.Edit}
        role={role}
        resetPassword={resetPassword}
      />

      <Redirect from="/" to="/all-orders" />
    </Switch>
  );
};

export default Routes;



// WEBPACK FOOTER //
// ./src/routes.jsx